/**
 * app-init.ts - Application Initialization
 *
 * Sets up event listeners and initializes the application on DOM ready.
 */

import { initializeWebSocketUpdates } from '../tournament/list';

/**
 * Setup all event listeners for the tournament application
 */
function setupEventListeners(): void {
  const form = document.getElementById('tournament-form');
  if (form) {
    form.addEventListener('submit', (e: Event) => {
      if (window.handleCreateTournament) {
        window.handleCreateTournament(e);
      }
    });
  }

  // Round info form
  const roundInfoForm = document.getElementById('round-info-form');
  if (roundInfoForm && window.handleRoundInfoSubmit) {
    roundInfoForm.addEventListener('submit', window.handleRoundInfoSubmit);
  }

  // Note: Create tournament button click handler is managed by auth-ui.ts
  // based on authentication state (opens auth modal when logged out,
  // tournament modal when logged in)

  // Show/hide numberOfRounds and double round-robin based on format selection
  const formatSelect = document.getElementById('tournament-format') as HTMLSelectElement | null;
  if (formatSelect && window.updateFormatConditionalFields) {
    formatSelect.addEventListener('change', () => {
      if (window.updateFormatConditionalFields) {
        window.updateFormatConditionalFields(formatSelect.value);
      }
    });
  }

  // Set up event delegation for standings round selector buttons (once on page load)
  const tournamentsContainer = document.getElementById('tournaments-container');
  if (tournamentsContainer && window.handleStandingsRoundClick) {
    tournamentsContainer.addEventListener('click', window.handleStandingsRoundClick);
  }

  // Also set up for single tournament page
  const tournamentContainer = document.getElementById('tournament-container');
  if (tournamentContainer && window.handleStandingsRoundClick) {
    tournamentContainer.addEventListener('click', window.handleStandingsRoundClick);
  }

  // Close modals when clicking outside
  window.addEventListener('click', (event: MouseEvent) => {
    const tournamentModal = document.getElementById('tournament-modal');
    if (event.target === tournamentModal && window.closeTournamentModal) {
      window.closeTournamentModal();
    }
  });

  // Reload tournaments when language changes
  window.addEventListener('languageChanged', () => {
    // Determine which page we're on and reload appropriately
    const isTournamentDetailPage = window.location.pathname.includes('/tournament.html');
    const isMyTournamentsPage = window.location.pathname.includes('/mytournaments.html') ||
                                window.location.pathname.includes('/mytournaments') ||
                                window.location.pathname === '/' ||
                                window.location.pathname === '/index.html';

    if (isTournamentDetailPage) {
      // On detail page, reload the single tournament
      const urlParams = new URLSearchParams(window.location.search);
      const tournamentId = urlParams.get('id');
      if (tournamentId && window.loadSingleTournament) {
        window.loadSingleTournament(tournamentId);
      }
    } else if (isMyTournamentsPage && window.loadTournamentsList) {
      // On mytournaments page, reload the list view
      window.loadTournamentsList();
    }
  });
}

/**
 * Initialize the application on DOM ready
 */
document.addEventListener('DOMContentLoaded', () => {
  setupEventListeners();

  // Check if we're on a page that needs the main tournament list
  // (not the public tournaments directory page)
  const isMainAppPage = document.getElementById('tournaments-container') !== null;
  const isDirectoryPage = document.getElementById('tournaments-directory') !== null;
  const isTournamentDetailPage = document.getElementById('tournament-container') !== null;

  // Handle single tournament detail page
  if (isTournamentDetailPage && !isMainAppPage) {
    const urlParams = new URLSearchParams(window.location.search);
    const tournamentId = urlParams.get('id');

    if (tournamentId && window.loadSingleTournament) {
      // Wait for auth to be ready before loading tournament
      // This ensures getCurrentUser() returns the correct user when rendering
      // so that canEditResults() works correctly and shows dropdowns for owners
      const loadTournament = () => window.loadSingleTournament(tournamentId);

      // Check if auth is already ready (getCurrentUser returns non-null or auth check completed)
      if (window.getCurrentUser && window.getCurrentUser()) {
        // Auth already ready, load immediately
        loadTournament();
      } else {
        // Wait for authReady event
        window.addEventListener('authReady', loadTournament, { once: true });
      }
    } else {
      const container = document.getElementById('tournament-container');
      if (container) {
        container.innerHTML = '<p class="error-message">No tournament ID specified</p>';
      }
    }
  }

  // Only initialize WebSocket and load tournaments if we're on the main app page
  if (isMainAppPage && !isDirectoryPage) {
    // Initialize WebSocket real-time updates
    initializeWebSocketUpdates();

    // Wait for auth to be ready before loading tournaments
    // This prevents flickering by ensuring we only load tournaments after auth check
    window.addEventListener('authReady', () => {
      const urlParams = new URLSearchParams(window.location.search);
      const isDemoView = urlParams.has('demo');
      const isLoggedIn = window.getCurrentUser && window.getCurrentUser();
      const isTournamentDetailPage = window.location.pathname.includes('/tournament.html');
      const isMyTournamentsPage = window.location.pathname.includes('/mytournaments.html') ||
                                  window.location.pathname.includes('/mytournaments') ||
                                  window.location.pathname === '/' ||
                                  window.location.pathname === '/index.html';

      // Load tournaments if user is logged in OR if viewing a demo
      if ((isLoggedIn || isDemoView) && isMyTournamentsPage && window.loadTournamentsList) {
        // On mytournaments page, use the simplified list view
        window.loadTournamentsList();
      }
    }, { once: true });

    // Reload tournaments when language changes to update all translations
    window.addEventListener('languageChanged', () => {
      const isTournamentDetailPage = window.location.pathname.includes('/tournament.html');
      const isMyTournamentsPage = window.location.pathname.includes('/mytournaments.html') ||
                                  window.location.pathname.includes('/mytournaments') ||
                                  window.location.pathname === '/' ||
                                  window.location.pathname === '/index.html';

      if (isTournamentDetailPage) {
        // On detail page, reload the single tournament
        const urlParams = new URLSearchParams(window.location.search);
        const tournamentId = urlParams.get('id');
        if (tournamentId && window.loadSingleTournament) {
          window.loadSingleTournament(tournamentId);
        }
      } else if (isMyTournamentsPage && window.loadTournamentsList) {
        // On mytournaments page, reload the list view
        window.loadTournamentsList();
      }
    });
  }
});
